import { useMutation, useQueryClient } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { readComplianceBody } from "../helper/readComplianceBody";
import { GET_DUPLICATE_IDENTITY_GROUPS_QUERY_KEY } from "./useGetDuplicateIdentityGroups";
import { GET_DUPLICATE_IDENTITY_GROUP_DETAIL_QUERY_KEY } from "./useGetDuplicateIdentityGroupDetail";
import type {
  DuplicateIdentityGroupDetail,
  DuplicateIdentityStatus,
} from "./types";

type StoredToken = { AccessToken?: string };

export type PatchDuplicateIdentityGroupPayload = {
  group_id: string;
  status?: Extract<
    DuplicateIdentityStatus,
    "support_contacted" | "escalated" | "ignored"
  >;
  notes?: string;
};

const patchDuplicateIdentityGroup = async (
  payload: PatchDuplicateIdentityGroupPayload,
): Promise<DuplicateIdentityGroupDetail | undefined> => {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken,
  );

  const { group_id, ...body } = payload;
  const url = API_ROUTES.PATCH_DUPLICATE_IDENTITY_GROUP.replace(
    "{group_id}",
    encodeURIComponent(group_id),
  );

  const response = await PrivateAdminPanelInstance.patch(url, body);
  return readComplianceBody<DuplicateIdentityGroupDetail>(response);
};

export const usePatchDuplicateIdentityGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: patchDuplicateIdentityGroup,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [GET_DUPLICATE_IDENTITY_GROUPS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [
          GET_DUPLICATE_IDENTITY_GROUP_DETAIL_QUERY_KEY,
          variables.group_id,
        ],
      });
    },
  });
};
